/**
 * Locales for markets whose language cannot be inferred from the country code
 * alone. Countries absent from this map are handled by `getLocaleFromCountryCode`.
 */
const countryCodeToLocale: Record<string, string> = {
  US: "en-US",
  GB: "en-GB",
  IE: "en-IE",
  FR: "fr-FR",
  DE: "de-DE",
  ES: "es-ES",
  IT: "it-IT",
  AU: "en-AU",
};

/** Used when neither the venue nor its country supplies a currency. */
const DEFAULT_CURRENCY = "GBP";

/** Used when no country is known for locale selection. */
const DEFAULT_COUNTRY_CODE = "GB";

/** Used when a country code is missing or not a usable ISO country code. */
const DEFAULT_LOCALE = "en-US";

/** An ISO 3166-1 alpha-2 country code, the only form Intl accepts as a region. */
const ISO_COUNTRY_CODE = /^[A-Za-z]{2}$/;

/**
 * Return the first candidate that carries a value, treating blank strings as
 * absent so an empty API field falls through to the next candidate.
 */
const firstPresent = (candidates: (string | null | undefined)[], fallback: string): string =>
  candidates.map((candidate) => candidate?.trim() ?? "").find((candidate) => candidate !== "") ?? fallback;

/**
 * Resolve the currency amounts are charged and displayed in. Candidates are
 * given in order of authority — the venue's own currency first, since a venue
 * may trade in a currency that differs from its country's default (e.g. a GB
 * venue charging in USD). The currency is never derived from a country code:
 * that only selects the number locale.
 */
export const resolveCurrencyCode = (...candidates: (string | null | undefined)[]): string =>
  firstPresent(candidates, DEFAULT_CURRENCY);

/** Resolve the country code used as the locale hint for number formatting. */
export const resolveCountryCode = (...candidates: (string | null | undefined)[]): string =>
  firstPresent(candidates, DEFAULT_COUNTRY_CODE);

/**
 * Resolve the formatting locale for a Collins country code.
 *
 * Collins can return any ISO country code, not just the markets listed in
 * `countryCodeToLocale`. An unlisted code is paired with English rather than
 * being treated as the US, so that Intl still applies that country's own
 * conventions -- an Irish or South African venue reads times on a 24-hour
 * clock, a New Zealand one on a 12-hour clock. Only the language is assumed.
 *
 * Accepts a nullish code because Collins may omit the field entirely.
 */
export const getLocaleFromCountryCode = (countryCode: string | null | undefined): string => {
  const normalised = countryCode?.trim().toUpperCase() ?? "";
  const mappedLocale = countryCodeToLocale[normalised];
  if (mappedLocale) return mappedLocale;

  return ISO_COUNTRY_CODE.test(normalised) ? `en-${normalised}` : DEFAULT_LOCALE;
};

export const formatCurrency = (value: number, currency: string, countryCode: string): string => {
  const locale = getLocaleFromCountryCode(countryCode);
  const resolvedCurrency = resolveCurrencyCode(currency);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: resolvedCurrency,
    currencyDisplay: "narrowSymbol",
  }).format(value);
};

export const formatDate = (dateString: string, countryCode: string, format: "short" | "long" = "short"): string => {
  if (!dateString) return "";

  const locale = getLocaleFromCountryCode(countryCode);
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return dateString;

  const options: Intl.DateTimeFormatOptions =
    format === "long"
      ? { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }
      : { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC" };

  return date.toLocaleDateString(locale, options);
};

/**
 * Arbitrary date used to turn an 'HH:mm' time of day into a Date. Only the
 * hour and minute reach the output, so the date itself is never displayed.
 */
const TIME_OF_DAY_REFERENCE_DATE = "2000-01-01";

/**
 * Format a standalone 'HH:mm' time of day for the country's locale, e.g.
 * '19:00' for GB and IE, '7:00 PM' for US. A supplied `locale` (the widget's
 * browser locale) takes the country's place.
 *
 * The clock is not chosen here: `hour12` is deliberately left unset so Intl
 * applies the locale's own hour cycle, which is defined for every region.
 *
 * Used where a time has no accompanying date -- such as the booking form's
 * time and end-time dropdown options, which are generated from the venue's
 * opening hours rather than from a booking.
 */
export const formatTimeOfDay = (timeString: string, countryCode: string, locale?: string): string => {
  if (!timeString) return "";

  const resolvedLocale = locale ?? getLocaleFromCountryCode(countryCode);
  const dateTime = new Date(`${TIME_OF_DAY_REFERENCE_DATE}T${timeString}`);

  if (Number.isNaN(dateTime.getTime())) return timeString;

  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
  };

  return dateTime.toLocaleTimeString(resolvedLocale, options);
};

export const formatTime = (timeString: string, dateString: string, countryCode: string, locale?: string): string => {
  if (!timeString || !dateString) return "";

  // The date never reaches the output, but an unparseable one still means the
  // pair cannot be trusted as a time, so the raw value is returned unchanged.
  const normalisedDate = dateString.includes("T") ? dateString.split("T")[0] : dateString;
  if (Number.isNaN(new Date(`${normalisedDate}T${timeString}`).getTime())) return timeString;

  return formatTimeOfDay(timeString, countryCode, locale);
};

/**
 * Simplified date formatting function for consistent ISO 8601 dates from APIs
 * Replaces the complex formatBookingDate logic since APIs now return consistent dates
 *
 * @param dateString - ISO 8601 date string from API (e.g., "2026-05-19T23:00:00+00:00")
 * @param countryCode - Country code for locale formatting (e.g., "GB", "US")
 * @param format - Output format: "short" (DD/MM/YYYY) or "long" (e.g., "19 May 2026")
 * @param venueTimezone - Optional venue timezone (e.g., "Asia/Tokyo") for timezone-aware formatting
 * @returns Formatted date string in the appropriate locale
 */
export const formatBookingDate = (
  dateString: string | null | undefined,
  countryCode: string,
  format: "short" | "long" = "short",
  venueTimezone?: string,
): string => {
  if (!dateString) return "";

  const locale = getLocaleFromCountryCode(countryCode);

  // A date-only value is a calendar date, not an instant: Collins sends the day the
  // booking is on, with the time carried separately. Pinning it to midnight UTC and
  // re-rendering it in the venue's timezone can only shift it — a negative-offset
  // venue (e.g. America/New_York) would show the previous day — so such values are
  // formatted as-is. Values that carry a time do describe an instant and are
  // rendered in the venue's timezone as before.
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString.trim());

  try {
    // Parse the date and extract the date components in UTC to avoid timezone conversion issues
    // If the date string doesn't have timezone info, treat it as UTC
    const normalizedDateString =
      dateString.includes("Z") || dateString.includes("+") || dateString.includes("-", 10)
        ? dateString
        : `${dateString}Z`; // Append Z to indicate UTC if no timezone specified

    const date = new Date(normalizedDateString);

    if (Number.isNaN(date.getTime())) {
      console.warn(`Invalid date string provided to formatBookingDate: ${dateString}`);
      return dateString;
    }

    // Use venue timezone if provided, otherwise fall back to UTC for backward compatibility
    const timeZone = isDateOnly ? "UTC" : (venueTimezone ?? "UTC");

    const options: Intl.DateTimeFormatOptions =
      format === "long"
        ? { month: "long", day: "numeric", year: "numeric", timeZone }
        : { year: "numeric", month: "2-digit", day: "2-digit", timeZone };

    // Format the date using the specified timezone
    return date.toLocaleDateString(locale, options);
  } catch (error) {
    console.error(`Error formatting date in formatBookingDate:`, error);
    return dateString;
  }
};
